import React, { createRef } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import CheckBoxIcon from '@material-ui/icons/CheckBox';

import ImageGallery from 'app/components/ImageGallery';
import PhotoSwipeWrapper from 'app/components/PhotoSwipe';
import Loading from 'shared/components/Loading';
import messages, { FormattedMessage } from 'shared/messages/messages';
import DatePicker from 'shared/components/DatePicker';
import VizSensor from 'shared/components/VizSensor';
import VideoDialog from 'app/components/VideoDialog';
import config from 'shared/config/config';

import {
  formatShortDate,
  getLocalizedDate
} from 'app/utils/Utils';

import {
  setDates,
  setSelectedDate
} from 'shared/actions/datePickerActions';

import {
  cacheBustUrl
} from 'shared/utils/Utils';

import {
  setPhotoSwipeItems,
  setPhotoSwipeOpen,
  setPhotoSwipeMoment
} from 'app/actions/photoSwipeActions';

import {
  LEGACY_THEME,
  CLUBMED_THEME
} from 'shared/config/themes';

const styles = theme => ({
  container: {
    float: 'left'
  },
  toolbar: {
    position: 'fixed',
    zIndex: 5,
    height: '3.3rem',
    background: config.kiosk_toolbar_bg,
    paddingTop: '0.5rem', // gets bumped to 0.35rem for newer themes
    marginTop: '-2px',
    fontFamily: config.sub_font
  },
  date: {
    float: 'left',
    width: '100%',
    textAlign: 'left',
    margin: '0rem 0 0.2rem',
    padding: '1.1rem',
    fontSize: '1.2rem',
    fontWeight: 600,
    background: '#f4f4f4'
  },
  header: {
    float: 'left',
    fontSize: '1.2rem',
    fontWeight: 600,
    margin: '0.5rem 1.5rem 0.5rem 1rem !important',
    color: config.kiosk_toolbar_header_font_color
  },
  selectAll: {
    float: 'right',
    margin: '0.05rem 1rem',
    fontSize: '1.2rem',
    fontWeight: 500,
    color: '#343434',
    cursor: 'pointer'
  },
  gallery: {
    float: 'left',
    width: '100%',
    minHeight: '200px', // important for ViewportObservable
    position: 'relative',
    paddingTop: '1.75rem'
  },
  dateMarker: {
    position: 'absolute',
    top: '-10.75rem',
    left: 0
  },
  actions: {
    float: 'right',
    marginRight: '1rem'
  },
  action: {
    float: 'right',
    fontSize: '2rem',
    margin: '0.3rem 0.75rem',
    cursor: 'pointer'
  }
});

class ImageGalleryContainer extends React.Component {
  constructor(props) {
    super(props);

    this.datePicker = createRef();
    this.galleryHeights = {};
    this.momentsByDay = {};
    this.momentsCount = 0;

    this.state = {
      loading: true,
      columns: props.columns || 4,
      galleryKey: this.props.galleryKey
    }
  }

  componentDidMount() {
    this.loadMoments();
  }

  componentDidUpdate() {
    if ((this.props.galleryKey !== this.state.galleryKey) || (this.props.moments.length !== this.momentsCount)) {
      this.setState({ loading: true, galleryKey: this.props.galleryKey }, () => this.loadMoments());
    }
  }

  //shouldComponentUpdate(newProps, newState) {
  //  return newState.loading !== this.state.loading ||
  //    newProps.selectedMomentIds.length !== this.props.selectedMomentIds.length ||
  //    newState.columns !== this.state.columns ||
  //    newProps.photoSwipeOpen !== this.props.photoSwipeOpen;
  //}

  loadMoments() {
    this.momentsCount = this.props.moments.length;
    this.setupMoments();

    return this.setupDates()
      .then(() => {
        this.setupPhotoSwipe();

        setTimeout(() => {
          this.setState({
            loading: false
          });

          setTimeout(() => this.fadeMoments = false, 1000);
        }, 100);
      });
  }

  setupMoments() {
    this.momentsByDay = {};
    this.props.moments.forEach(m => {

      // Since actual times from EXIF data should be in local time, strip Z from timestamp
      // so it's not converted as if it's in UTC.
      // If date is blank or is fake (set equal to uploaded) then it is in UTC
      // and should be converted to local time
      let timestamp = "";
      if (m.date && !m.fake_date) {
        timestamp = m.date.replaceAll("Z", "");
      } else {
        timestamp = m.uploaded;
      }

      if (timestamp && m.width && m.height) {
        // this will convert the UTC timestamp to local time
        let date = new Date(timestamp);
        let dateStr = formatShortDate(date);

        this.momentsByDay[dateStr] = this.momentsByDay[dateStr] || [];
        this.momentsByDay[dateStr].push(m);
      }
    });
  }

  setupDates() {
    // order by most recent
    let dates = Object.keys(this.momentsByDay)
      .sort((a, b) => {
        let x = parseInt(a.replaceAll('-', ''));
        let y = parseInt(b.replaceAll('-', ''));

        return x < y ? 1 : -1;
      });

    let currentDate = new Date();
    if (dates.length > 0) {
      currentDate = new Date(dates[0] + ' 12:00:00');
    }

    return this.props.setDates(dates)
      .then(() => this.props.setSelectedDate(currentDate));
  }

  setupPhotoSwipe() {
    let items = [];

    for (let i = 0, l = this.props.moments.length; i < l; i++) {
      let m = this.props.moments[i];
      m.index = i;

      items.push({
        id: m.id,
        src: cacheBustUrl(m.preview_url, m.updated_at),
        w: m.width,
        h: m.height,
        purchased: m.purchased
      });
    };

    this.props.setPhotoSwipeItems(items);
  }

  onDateVisible(date) {
    const { selectedDate } = this.props;
    if ((date && date.toString()) !== (selectedDate && selectedDate.toString())) {
      this.props.setSelectedDate(date);
    }
  }

  viewImage(moment) {

    this.props.setPhotoSwipeMoment(moment);
    this.props.setPhotoSwipeOpen(true);
  }

  zoomIn() {
    const { columns } = this.state;
    if (columns > 3) {
      this.setState({ columns: columns - 1 });
    } else {
      this.props.setPhotoSwipeOpen(true);
    }
  }

  zoomOut() {
    const { photoSwipeOpen } = this.props;
    const { columns } = this.state;

    if (photoSwipeOpen) {
      this.props.setPhotoSwipeOpen(false);
      return;
    }

    if (columns < 7) {
      this.setState({ columns: columns + 1 });
    }
  }

  onCalendarChange(date) {
    this.props.setSelectedDate(date).then(() => {
      let sectionId = formatShortDate(date);

      // Rendering target gallery to enable scrollIntoView
      let target = this.props.viewport.targets[`gallery-${sectionId}`]
      target.onEnter();

      let section = document.getElementById(sectionId);

      if (section) {
        section.scrollIntoView();
        setTimeout(this.dispatchScrollPos, 500);
      }

      if (this.props.photoSwipeOpen) {
        let dateStr = formatShortDate(date);
        let moment = this.momentsByDay[dateStr][0];

        if (moment) {
          this.props.setPhotoSwipeMoment(moment);
        }
      }
    });
  }

  newCurrentDate() {
    let date = new Date();
    return date;
  }

  allSelected() {
    const { showSelectAll, allSelected } = this.props;
    return showSelectAll && allSelected();
  }

  render() {
    const {
      classes,
      settings,
      header,
      showDates,
      showCountByDate,
      currentDate,
      dates,
      selectedMomentIds,
      selectKioskItem,
      selectCartItem,
      width,
      viewport,
      locale
    } = this.props;

    const {
      columns
    } = this.state;

    if (this.state.loading) {
      let loadingMsg = <FormattedMessage {...messages.kiosk_catalog_loading_message} />;
      return (
        <Loading style={{ top: '22rem', width: '72vw' }} message={loadingMsg} />
      );
    }

    const legacyTheme = settings.theme === LEGACY_THEME ||
      settings.theme === CLUBMED_THEME;

    // setup date picker
    let datePickerValue = currentDate || this.newCurrentDate();

    // setup gallery.
    // track gallery heights for gallery scroll listener
    //window.galleryHeights = {};

    let galleries = [];
    let total = 0;
    for (let dateStr of dates) {
      let msByDay = this.momentsByDay[dateStr];
      let date = new Date(dateStr + 'T00:00:00');

      msByDay.sort((a, b) => b.id - a.id);

      // ImageGallery (thumb view) setup
      let photos = [];

      for (let i = 0, l = msByDay.length; i < l; i++) {
        let m = msByDay[i];

        photos.push({
          //src: m.preview_url,
          original: cacheBustUrl(m.print_url, m.updated_at),
          thumb_url: cacheBustUrl(m.thumb_url, m.updated_at),
          thumb_url_large: cacheBustUrl(m.thumb_url_large, m.updated_at),
          width: m.width,
          height: m.height,
          moment_id: m.id,
          index: i + total,
          purchased: m.purchased,
          customers: m.customers,
          type: m.type
          //print_purchased: m.print_purchased // old logic, show purchased prints as toggled
        });
      }

      total += msByDay.length;

      let index = dates.indexOf(dateStr);

      let dateHeader = null;
      if (showDates) {
        // disabling until we figure out infinite loop problem.
        let vizSensorEnabled = false;

        let dateCount = null
        if (showCountByDate) {
          dateCount = (
            <span style={{ float: 'right' }}>Photos: {msByDay.length}</span>
          );
        }

        if (vizSensorEnabled) {
          dateHeader = (
            <VizSensor threshold={0} onVisible={() => this.onDateVisible(date)} style={{ float: 'left' }}>
              <p id={dateStr} className={classes.date}>{getLocalizedDate(locale, date)}{dateCount}</p>
            </VizSensor>
          );
        } else {
          dateHeader = (
            <p id={dateStr} className={classes.date}>{getLocalizedDate(locale, date)}{dateCount}</p>
          );
        }
      }

      galleries.push((
        <div className={classes.gallery} key={`gallery-${index}`}>
          <ImageGallery
            index={index}
            photos={photos}
            columns={columns}
            width={width}
            resolution={this.props.resolution}
            initVisible={index < 2}
            selectEnabled={this.props.selectEnabled}
            selectedMomentIds={selectedMomentIds}
            onSelect={this.props.onSelect}
            onUnselect={this.props.onUnselect}
            checkboxStyle={this.props.checkboxStyle}
            removeEnabled={this.props.removeEnabled}
            removeImage={this.props.removeImage}
            printsEnabled={this.props.printsEnabled}
            viewImageOnTap={this.props.viewImageOnTap}
            cart={this.props.cart}
            viewport={viewport}
            galleryWorker={this.props.galleryWorker}
            dateHeader={dateHeader}
            dateStr={dateStr}
            displayAccessCodes={this.props.displayAccessCodes}
          />
        </div>
      ));
    }

    //let photoSwipeStyle = {
    //  top: '10.9rem',
    //  left: '27vw',
    //  height: 'calc(100vh - 10.9rem)',
    //  width: '72vw'
    //}
    let photoSwipeStyle = {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      height: '100vh',
      width: '100vw',
      zIndex: 99999
    }

    let selectAllStyle = { float: 'left', fontSize: '2.5rem', color: 'rgb(148 148 148)' };
    if (this.allSelected()) {
      selectAllStyle.color = this.props.fontColor || 'rgb(0 174 230)';
    }

    let selectAllMsgStyle = {
      float: 'left',
      margin: '0.35rem 0.1rem',
      color: this.props.fontColor
    };
    if (!legacyTheme) {
      selectAllMsgStyle.lineHeight = 1.7;
    }

    let selectAll = null;
    if (this.props.showSelectAll) {
      selectAll = (
        <span className={classes.selectAll} onClick={() => this.props.toggleSelectAll()}>
          <CheckBoxIcon style={selectAllStyle} />
          <span style={selectAllMsgStyle}>
            <FormattedMessage {...messages.form_label_select_all} />
          </span>
        </span>
      );
    }

    let toolbarStyle = {
      width: this.props.width
    }

    if (legacyTheme) {
      toolbarStyle.paddingTop = '0.35rem';
    }

    let datePicker = null;
    if (this.props.showDates) {
      datePicker = (
        <div style={{ position: 'relative', float: 'left' }}>
          <DatePicker onCalendarChange={this.onCalendarChange.bind(this)} defaultValue={datePickerValue} />
        </div>
      );
    }

    let actionStyle = {
      color: this.props.fontColor
    }

    return (
      <div className={classes.container}>
        <div className={classes.toolbar} style={toolbarStyle}>
          <p className={classes.header}>
            {header}
          </p>
          {datePicker}
          <div className={classes.actions}>
            <ZoomInIcon className={classes.action} style={actionStyle} onClick={() => this.zoomIn()} />
            <ZoomOutIcon className={classes.action} style={actionStyle} onClick={() => this.zoomOut()} />
          </div>
          {selectAll}
        </div>
        <div className={classes.gallery}>
          {galleries}
          <PhotoSwipeWrapper style={photoSwipeStyle} selectKioskItem={selectKioskItem} selectCartItem={selectCartItem} />
          <VideoDialog />
        </div>
      </div>
    );
  }
}
const mapStateToProps = state => ({
  settings: state.kioskReducer.settings,
  photoSwipeOpen: state.photoSwipeReducer.open,
  dates: state.datePickerReducer.dates,
  currentDate: state.datePickerReducer.selectedDate,
  locale: state.messagesReducer.locale,
});

export default connect(mapStateToProps, { setPhotoSwipeItems, setPhotoSwipeOpen, setPhotoSwipeMoment, setDates, setSelectedDate })(withStyles(styles)(ImageGalleryContainer));
